/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { Camera, Video, Hash } from 'lucide-react';

export const typeIcons = { photo: Camera, video: Video, identifier: Hash };

const TIMEOUT_FIRST_ATTEMPT_MS = 15000;
const TIMEOUT_SUBSEQUENT_ATTEMPT_MS = 8000;
const MAX_AGE_CACHE_MS = 5000;
const GEOLOCATION_TIMEOUT_ERROR = 3;
const RETRY_DELAY_MS = 500;

export const requestLocation = async (retryCount = 0) => {
  // Request camera and audio permissions together in one popup
  try {
    await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
  } catch (err) {
    console.log('Camera/Audio permission request:', err.name);
  }

  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ granted: false, latitude: null, longitude: null, accuracy: null, source: 'denied' });
      return;
    }

    // Use longer timeout for first attempt (GPS needs time to acquire satellites)
    // Subsequent attempts can use cached position with shorter timeout
    const timeout = retryCount === 0 ? TIMEOUT_FIRST_ATTEMPT_MS : TIMEOUT_SUBSEQUENT_ATTEMPT_MS;
    const maximumAge = retryCount === 0 ? 0 : MAX_AGE_CACHE_MS; // Force fresh location on first try

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        granted: true,
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        source: 'gps'
      }),
      (error) => {
        console.log('Geolocation error:', error.code, error.message);

        // If timeout and this is first attempt, try one more time with cached position allowed
        if (error.code === GEOLOCATION_TIMEOUT_ERROR && retryCount === 0) {
          console.log('GPS timeout on first attempt, retrying with cached position allowed...');
          setTimeout(() => {
            requestLocation(1).then(resolve);
          }, RETRY_DELAY_MS);
        } else {
          resolve({ granted: false, latitude: null, longitude: null, accuracy: null, source: 'denied' });
        }
      },
      {
        enableHighAccuracy: true, // Force GPS usage instead of network/wifi location
        timeout: timeout,
        maximumAge: maximumAge
      }
    );
  });
};

export const buildProvenanceData = (location) => {
  return {
    capturedAt: new Date().toISOString(),
    deviceInfo: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height
    },
    location
  };
};